import React from 'react';
import { Loader2 } from 'lucide-react';
import './QueryStateView.scss';

/**
 * QueryLoadingState Component
 * Displays rich loading skeletons or modern glowing spinner.
 *
 * @param {Object} props
 * @param {'skeleton'|'cards'|'table'|'timeline'|'spinner'} [props.variant='skeleton']
 * @param {string} [props.message='Loading data...']
 * @param {number} [props.count=3]
 * @param {string} [props.className]
 */
export const QueryLoadingState = ({
  variant = 'skeleton',
  message = 'Loading data...',
  count = 3,
  className = '',
}) => {
  if (variant === 'spinner') {
    return (
      <div className={`query-state-loading query-state-spinner ${className}`} role="status" aria-live="polite">
        <div className="query-spinner-ring">
          <Loader2 className="query-spinner-icon animate-spin" size={36} />
        </div>
        {message && <p className="query-loading-text">{message}</p>}
      </div>
    );
  }

  if (variant === 'cards') {
    return (
      <div className={`query-skeleton-cards ${className}`} role="status" aria-label="Loading cards">
        {Array.from({ length: count }).map((_, idx) => (
          <div key={idx} className="query-skeleton-card query-shimmer">
            <div className="query-skeleton-avatar" />
            <div className="query-skeleton-content">
              <div className="query-skeleton-line short" />
              <div className="query-skeleton-line medium" />
              <div className="query-skeleton-line full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'table') {
    return (
      <div className={`query-skeleton-table ${className}`} role="status" aria-label="Loading table">
        <div className="query-skeleton-header query-shimmer" />
        {Array.from({ length: count }).map((_, idx) => (
          <div key={idx} className="query-skeleton-row query-shimmer">
            <div className="query-skeleton-cell small" />
            <div className="query-skeleton-cell" />
            <div className="query-skeleton-cell" />
            <div className="query-skeleton-cell action" />
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'timeline') {
    return (
      <div className={`query-skeleton-timeline ${className}`} role="status" aria-label="Loading timeline">
        {Array.from({ length: count }).map((_, idx) => (
          <div key={idx} className="query-skeleton-timeline-item">
            <div className="query-skeleton-timeline-dot query-shimmer" />
            <div className="query-skeleton-timeline-body query-shimmer">
              <div className="query-skeleton-line short" />
              <div className="query-skeleton-line full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default 'skeleton' lines
  return (
    <div className={`query-state-loading query-skeleton-default ${className}`} role="status" aria-live="polite">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="query-skeleton-block query-shimmer">
          <div className="query-skeleton-line short" />
          <div className="query-skeleton-line full" />
          <div className="query-skeleton-line medium" />
        </div>
      ))}
      {message && <span className="sr-only">{message}</span>}
    </div>
  );
};

export default QueryLoadingState;
